import React, { useState, useEffect, useRef } from "react";

const JobTile = ({
  project,
  projectDetailClick,
  screenName,
  dragStarted,
  className = "",
  style = {},
  largeView,
  draggable = true,
  selectable = false,
  projectSelect,
  isParkedOriginal = false,
  onParkJob,
}) => {
  // Context menu state for parking
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const contextMenuRef = useRef(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ visible: false, x: 0, y: 0 });
      }
    };
    if (contextMenu.visible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [contextMenu.visible]);

  // Handle right-click to show park context menu
  const handleContextMenu = (event) => {
    // Only show context menu if onParkJob is provided (means it's parkable)
    if (onParkJob && !isParkedOriginal) {
      event.preventDefault();
      setContextMenu({ visible: true, x: event.clientX, y: event.clientY });
    }
  };

  // Handle park action from context menu
  const handleParkClick = () => {
    if (onParkJob) {
      onParkJob(project);
    }
    setContextMenu({ visible: false, x: 0, y: 0 });
  };
  const firstSpaceIndex = project?.encodedSmallTitle?.indexOf(" ");

  // add tooltips to emoji icons
  var tooltipText = "";
  tooltipText = project?.encodedSmallTitle?.includes("📦")
    ? `📦 expressShip\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🪑")
    ? tooltipText + `🪑 furniture\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("📅")
    ? tooltipText + `📅 prescheduled\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("⚡️")
    ? tooltipText + `⚡️ rush\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🏃🏽‍♂️")
    ? tooltipText + `🏃🏽‍♂️ expedite\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("✈️")
    ? tooltipText + `✈️ distant\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🛑")
    ? tooltipText + `🛑 IMS-warning\n`
    : tooltipText;

  return (
    <>
      {project?.extraCell ? (
        <div
          key={project.id}
          onClick={(event) =>
            selectable
              ? projectSelect(project, event)
              : projectDetailClick(project, event)
          }
          className={`text-sm-2 min-h-6 px-2 flex bg-attribute-data rounded relative ${
            project.selected && "border-[bg-skin-base-hover]"
          } ${className}`}
        >
          {selectable && (
            <div
              className="border rounded w-[20px] h-[20px] flex items-center justify-center absolute top-1 right-1"
              title="Details"
              onClick={(event) => {
                event.stopPropagation();
                return projectDetailClick(project, event);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[16px] h-[16px] fill-skin-base"
              >
                <g id="SVGRepo_bgCarrier" strokewidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                </g>
              </svg>
            </div>
          )}
          <span
            className={`self-center font-medium ${
              screenName === "optimizer" ? "" : "text-black"
            }`}
          >
            {
              // 2025-02-03 - added text-black to this span to force darkMode extraCell text to be black, Scrum request
              largeView
                ? project?.encodedLargeTitle
                : project?.encodedSmallTitle
            }
          </span>
        </div>
      ) : project?.attribute ? (
        <div
          key={project.id}
          className={`text-sm-2 min-h-6 px-2 flex bg-attribute-data rounded ${className}`}
        >
          <span className="self-center font-medium text-black">
            {
              // 2025-02-03 - added text-black to this span to force darkMode extraCell text to be black, Scrum request
              largeView
                ? project?.encodedLargeTitle
                : project?.encodedSmallTitle
            }
          </span>
        </div>
      ) : (
        <div
          key={project?.id}
          draggable={isParkedOriginal ? false : draggable}
          onClick={(event) =>
            selectable
              ? projectSelect(project, event)
              : projectDetailClick(project, event)
          }
          onDragStart={() => !isParkedOriginal && dragStarted(project?.id, project?.invoiceNo)}
          onContextMenu={handleContextMenu}
          className={`text-sm-2 min-h-6 flex relative
            ${isParkedOriginal
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:bg-skin-base-hover"
            }
            ${
              project.selected && "outline -outline-offset-1 outline-yellow"
            } ${className}`}
          style={style}
        >
          {selectable && (
            <div
              className="border rounded w-[20px] h-[20px] flex items-center justify-center absolute top-1 right-1"
              title="Details"
              onClick={(event) => {
                event.stopPropagation();
                return projectDetailClick(project, event);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[16px] h-[16px] fill-skin-base"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                </g>
              </svg>
            </div>
          )}
          <div
            className={`bg-surface-${project?.surface
              ?.split(" ")
              .join("-")} badge`}
          ></div>
          {project?.installer === "" && project?.extraCellText
            ? project?.extraCellText.substring(0, 20)
            : ""}
          {largeView ? (
            <span className="mx-2 self-center font-medium whitespace-pre-line  first-line:font-bold">
              {project?.encodedLargeTitle}
            </span>
          ) : tooltipText !== "" ? (
            <span className="mx-2 self-center font-medium">
              <span className="font-bold tooltip">
                {project?.encodedSmallTitle?.substring(0, firstSpaceIndex)}
                <span className="tooltiptext">{tooltipText}</span>
              </span>
              {project?.encodedSmallTitle?.substring(firstSpaceIndex)}
            </span>
          ) : (
            <span className="mx-2 self-center font-medium">
              <span className="font-bold">
                {project?.encodedSmallTitle?.substring(0, firstSpaceIndex)}
              </span>
              {project?.encodedSmallTitle?.substring(firstSpaceIndex)}
            </span>
          )}
        </div>
      )}

      {/* Context menu for parking */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            backgroundColor: "white",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            padding: "4px 0",
            minWidth: "140px",
          }}
        >
          <button
            onClick={handleParkClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: "8px 16px",
              textAlign: "left",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              color: "#111827",
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          >
            {/* Parking icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              style={{ fill: "#f97316", flexShrink: 0 }}
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2h-1v3h-2V7h1zm1 4h1V9h-1v2z"/>
            </svg>
            <span>Park Job</span>
          </button>
        </div>
      )}
    </>
  );
};

export default JobTile;
